import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../../styles/designTokens";

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  min-height: 100vh;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => hairline(theme, true)};
    border-radius: 2px;
  }

  /* Botão Voltar */
  .back-button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    margin-bottom: 8px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
    font-size: 0.78em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    ${mono};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    width: fit-content;

    &:hover {
      opacity: 0.85;
      background: ${({ theme }) => surface(theme, 1)};
    }

    svg { font-size: 0.95em; }
  }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 0 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "CLIENTE · DETALHE";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }
    }

    .header-meta {
      display: flex;
      gap: 10px;
      align-items: center;
      font-size: 0.78em;
    }
  }

  .status-tag {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    ${mono};
    font-size: 0.95em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${tokens.brand};

    &::before {
      content: "";
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${tokens.brand};
      box-shadow: 0 0 8px ${tokens.brandGlow};
      animation: ${pulseDot} 2.2s ease-in-out infinite;
    }
  }

  .update-tag {
    ${mono};
    color: ${({ theme }) => theme.text};
    opacity: 0.4;
    font-size: 0.9em;

    &::before {
      content: "· ";
      opacity: 0.5;
    }
  }

  /* ========== TABS ========== */
  .tabs-container {
    display: flex;
    gap: 4px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      background: transparent;
      border: none;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all 200ms ${tokens.easeOut};

      svg { font-size: 0.95em; }

      &:hover { opacity: 0.9; }

      &.active {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        opacity: 1;
      }
    }
  }

  /* ========== KPI GRID ========== */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    &.kpi-grid-5 { grid-template-columns: repeat(5, 1fr); }
    &.kpi-grid-4 { grid-template-columns: repeat(4, 1fr); }
    &.kpi-grid-3 { grid-template-columns: repeat(3, 1fr); }
    &.kpi-grid-2 { grid-template-columns: repeat(2, 1fr); }

    @media (max-width: 1400px) {
      grid-template-columns: repeat(3, 1fr);
      &.kpi-grid-5 { grid-template-columns: repeat(3, 1fr); }
      &.kpi-grid-4 { grid-template-columns: repeat(2, 1fr); }
      &.kpi-grid-3 { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      &.kpi-grid-5,
      &.kpi-grid-4,
      &.kpi-grid-3,
      &.kpi-grid-2 { grid-template-columns: 1fr; }
    }

    .kpi-card {
      ${({ theme }) => cardBase(theme)};
      padding: 16px 18px 14px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 96px;
      position: relative;
      overflow: hidden;

      &:hover {
        border-color: ${({ theme }) => hairline(theme, true)};
        background: ${({ theme }) => surface(theme, 1)};
      }

      .card-content {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .thumb {
          opacity: 0.35;
          font-size: 0.9em;
          color: ${({ theme }) => theme.text};
          display: inline-flex;
          align-items: center;
        }

        .text {
          display: flex;
          flex-direction: column;
          gap: 2px;

          p { margin: 0; }

          p:first-child {
            ${mono};
            font-size: 1.5em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            letter-spacing: -0.02em;
            line-height: 1.1;
          }

          p:last-child {
            font-size: 0.72em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            margin-top: 2px;
            letter-spacing: -0.005em;
          }
        }
      }

      &.primary .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
      &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
      &.warning .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
      &.danger  .card-content .thumb { color: ${tokens.danger}; opacity: 0.65; }
      &.info    .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
      &.tax-cbs .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
      &.tax-ibs .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    }
  }

  /* ========== CHARTS ROW 50/50 ========== */
  .charts-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 14px;

    @media (max-width: 1100px) { grid-template-columns: 1fr; }

    .chart-half {
      min-height: 360px;
    }
  }

  /* ========== CARDS DE SEÇÃO ========== */
  .section-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;

      .header-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.01em;

        svg { font-size: 0.95em; opacity: 0.5; }
      }

      .header-badge {
        ${mono};
        font-size: 0.65em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
    }
  }

  /* Contact & Metrics */
  .contact-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .contact-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 8px 0;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      &:last-child { border-bottom: none; }

      svg {
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        opacity: 0.35;
        margin-top: 2px;
        flex-shrink: 0;
      }

      .contact-info {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .contact-label {
          ${mono};
          font-size: 0.6em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
        }

        .contact-value {
          font-size: 0.82em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.9;
        }
      }
    }
  }

  .metrics-list {
    display: flex;
    flex-direction: column;
    gap: 0;

    .metric-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 10px 0;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      &:last-child { border-bottom: none; }

      .metric-label {
        font-size: 0.78em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .metric-value {
        ${mono};
        font-size: 0.82em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.9;
      }
    }
  }

  /* ========== CHARTS ========== */
  .chart-container {
    flex: 1;
    min-height: 260px;
  }

  .donut-wrapper {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;

    .donut-chart {
      position: relative;
      width: 180px;
      height: 180px;
      flex-shrink: 0;

      svg { display: block; }
    }

    .donut-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;

      .donut-total {
        ${mono};
        font-size: 1.1em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.02em;
      }

      .donut-label {
        font-size: 0.6em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
    }
  }

  .categories-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;

    .category-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 0;

      .category-color {
        width: 8px;
        height: 8px;
        border-radius: 2px;
        flex-shrink: 0;
      }

      .category-info {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        min-width: 0;

        .category-name {
          font-size: 0.78em;
          color: ${({ theme }) => theme.text};
          opacity: 0.85;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .category-percent {
          ${mono};
          font-size: 0.7em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }
      }

      .category-value {
        ${mono};
        font-size: 0.74em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
      }
    }
  }

  /* ========== PRODUCTS TABLE (estilo Produto Detalhe) ========== */
  .products-table {
    padding: 0;

    .table-header {
      display: grid;
      grid-template-columns: 2fr 1.2fr 1.2fr 1.5fr 0.8fr;
      gap: 12px;
      padding: 14px 20px;
      background: ${({ theme }) => surface(theme, 1)};
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      font-size: 11px;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .table-row {
      display: grid;
      grid-template-columns: 2fr 1.2fr 1.2fr 1.5fr 0.8fr;
      gap: 12px;
      padding: 16px 20px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      transition: all 0.2s ease;
      align-items: center;

      &:hover {
        background: ${({ theme }) => surface(theme, 1)};
      }

      &:last-child {
        border-bottom: none;
      }
    }

    .col-produto,
    .col-quantidade,
    .col-icms,
    .col-valor,
    .col-participacao {
      display: flex;
      align-items: center;
    }

    .product-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .product-color {
        width: 8px;
        height: 8px;
        border-radius: 2px;
        flex-shrink: 0;
      }

      .product-details {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;

        .product-name {
          font-size: 13px;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .product-code {
          font-size: 11px;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }
      }
    }

    .quantity-badge {
      font-size: 12px;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      opacity: 0.8;
    }

    .value-text {
      font-size: 13px;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }

    .participation-bar {
      position: relative;
      width: 100%;
      height: 24px;
      background: ${({ theme }) => surface(theme, 2)};
      border-radius: 6px;
      overflow: hidden;

      .participation-fill {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        border-radius: 6px;
        transition: width 0.3s ease;
        opacity: 0.3;
      }

      .participation-text {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        font-size: 11px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }
    }
  }

  /* ========== TIMELINE REFORMA ========== */
  .reform-timeline {
    display: flex !important;
    gap: 8px;
    padding: 0;
    margin-bottom: 14px;
    overflow-x: auto;
    width: 100%;
    min-height: 56px;
    flex-shrink: 0;

    &::-webkit-scrollbar { height: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 2px;
    }

    .timeline-item {
      display: flex !important;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      padding: 8px 16px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      flex-shrink: 0;
      min-width: 100px;
      position: relative;
      outline: none;

      .timeline-year {
        display: block;
        font-size: 1.2em;
        font-weight: 600;
        font-family: 'Poppins', system-ui, sans-serif;
        color: ${({ theme }) => theme.text};
        opacity: 0.55;
        letter-spacing: -0.02em;
      }

      .timeline-label {
        display: block;
        font-size: 0.7em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        text-align: center;
        white-space: nowrap;
      }

      &:hover {
        background: ${({ theme }) => surface(theme, 1)};
        border-color: ${({ theme }) => hairline(theme, true)};

        .timeline-year { opacity: 0.85; }
        .timeline-label { opacity: 0.65; }
      }

      &.active {
        background: ${tokens.brandSoft};
        border: 1px solid ${tokens.brandRing};
        border-left: 3px solid ${tokens.brand};

        .timeline-year {
          color: ${tokens.brand};
          opacity: 1;
        }

        .timeline-label {
          color: ${({ theme }) => theme.text};
          opacity: 1;
        }
      }
    }
  }

  /* ========== STATUS PILLS ========== */
  .status-pill {
    ${mono};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: ${tokens.radiusPill};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border: 1px solid transparent;
    white-space: nowrap;

    &.ok {
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      border-color: ${tokens.brandRing};
    }
    &.warn {
      background: rgba(245, 158, 11, 0.08);
      color: ${tokens.warn};
      border-color: rgba(245, 158, 11, 0.22);
    }
    &.danger {
      background: ${tokens.dangerSoft};
      color: ${tokens.danger};
      border-color: rgba(239, 68, 68, 0.22);
    }
  }

  /* Alias para compatibilidade com aba Reforma */
  .section-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    align-items: start;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .stats-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;

    .card-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;

      .header-title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.01em;

        svg { font-size: 0.95em; opacity: 0.5; }
        h4 { margin: 0; font-size: 1em; font-weight: 600; }
      }
    }
  }

  .reform-info-content {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .reform-info-item {
      padding: 12px 14px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      border-left: 3px solid ${tokens.brand};

      h5 {
        margin: 0 0 6px 0;
        font-size: 0.82em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }

      p {
        margin: 0;
        font-size: 0.78em;
        color: ${({ theme }) => theme.text};
        opacity: 0.7;
        line-height: 1.5;
      }
    }
  }
`;
